import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PageHeader, LinkButton, SectionTitle } from "@/components/site/kit";
import { Reveal, Marquee } from "@/components/motion/primitives";
import { getStats } from "@/lib/api";
import { ArrowUpRight, Code, Search, PenTool, Megaphone, Cpu, LineChart } from "lucide-react";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";

const PORTRAIT = "/portraits/julius-about.png";

const SKILLS = [
  { icon: Code, label: "Web & App Development" },
  { icon: Search, label: "SEO & Analytics" },
  { icon: Megaphone, label: "Digital Marketing" },
  { icon: PenTool, label: "Writing & Content" },
  { icon: Cpu, label: "AI & Automation" },
  { icon: LineChart, label: "Growth Strategy" },
];

const JOURNEY = [
  { year: "2016", title: "First lines of code", body: "Started building websites for local businesses and fell in love with turning ideas into working products." },
  { year: "2019", title: "Going digital-first", body: "Built a fully remote consultancy serving clients across borders and languages — no calls, just results." },
  { year: "2022", title: "Launched Mason Academy", body: "Began teaching what I build: languages, business, and technology, reaching learners in dozens of countries." },
  { year: "2024", title: "The Toolkit", body: "Launched a suite of free online tools — while continuing to write, teach, and help clients grow." },
];

export default function About() {
  const [stats, setStats] = useState({});
  useEffect(() => { getStats().then(setStats).catch(() => {}); }, []);

  return (
    <div data-testid="about-page">
      <SEO
        title="About Julius Mason — Web Developer & Digital Consultant"
        description="Julius Mason is an independent web developer building professional websites for businesses worldwide. Learn about the journey, the approach, and Mason Academy."
        path="/about"
        lang="en"
        keywords="julius mason, web developer, about, digital consultant, mason academy founder"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ])}
      />
      <PageHeader
        chapter="About / Julius Mason"
        titleLines={["A builder,", "teacher &", "digital native."]}
        description="I'm Julius Mason — a digital entrepreneur, educator, author, and technology consultant. I help businesses and people grow, working entirely online across many languages."
      />

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-border">
              <img src={PORTRAIT} alt="Julius Mason" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7 space-y-12">
            <Reveal>
              <p className="overline text-primary mb-4">Biography</p>
              <p className="text-xl md:text-2xl leading-relaxed">
                Over the last decade I've helped founders and businesses turn ideas into revenue — designing websites,
                building apps, running marketing, and writing the words that convert. I work digitally by design, which
                lets me serve clients in any language, in any timezone, without the friction of calls.
              </p>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-10">
              <Reveal>
                <p className="overline text-primary mb-3">Mission</p>
                <p className="text-muted-foreground leading-relaxed">To make world-class technology, marketing, and education accessible to any entrepreneur, anywhere — regardless of language or location.</p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="overline text-primary mb-3">Philosophy</p>
                <p className="text-muted-foreground leading-relaxed">Ship things that move real numbers. Communicate clearly in writing. Teach generously. Let the work speak louder than any sales pitch.</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
          {[["Projects", stats.projects_completed], ["Years", stats.years_experience], ["Clients", stats.satisfied_clients], ["Products", stats.products_created]].map(([l, v], i) => (
            <Reveal key={l} delay={i * 0.08} className="py-10 px-6 first:pl-0">
              <div className="font-display text-5xl md:text-6xl text-primary">{v ?? "—"}<span className="text-foreground">+</span></div>
              <div className="mt-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">{l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SKILLS */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <SectionTitle overline="Skills & Expertise" title="What I bring to the table" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SKILLS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06} className="flex items-center gap-4 border border-border p-6 hover:border-primary transition-colors">
                <s.icon className="w-8 h-8 text-primary" strokeWidth={1.4} />
                <span className="font-display text-xl">{s.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="py-20 md:py-28 bg-secondary/20 border-y border-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <SectionTitle overline="Entrepreneurial Journey" title="The road so far" />
          <div className="space-y-0">
            {JOURNEY.map((j, i) => (
              <Reveal key={j.year} delay={i * 0.08}>
                <div className="grid md:grid-cols-12 gap-4 py-8 border-t border-border last:border-b group hover:bg-background/40 transition-colors">
                  <span className="md:col-span-2 font-mono text-primary text-lg">{j.year}</span>
                  <h3 className="md:col-span-3 font-display text-2xl">{j.title}</h3>
                  <p className="md:col-span-7 text-muted-foreground leading-relaxed">{j.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee className="py-8 border-b border-border">
        <div className="flex items-center gap-8 pr-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="font-display text-4xl md:text-6xl text-stroke whitespace-nowrap">Empowering People<span className="text-primary text-stroke-primary"> · </span></span>
          ))}
        </div>
      </Marquee>

      <section className="py-24 text-center">
        <div className="mx-auto max-w-3xl px-5">
          <h2 className="font-display text-4xl md:text-6xl mb-8">Want to work together?</h2>
          <LinkButton to="/contact" primary>Get in touch <ArrowUpRight className="w-5 h-5" /></LinkButton>
        </div>
      </section>

      {/* Related pages so About visitors can dig deeper without touching the nav. */}
      <section className="py-16 border-t border-border bg-secondary/30" data-testid="about-related">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <p className="overline text-primary mb-8">Keep exploring</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: "/aveno", label: "AVENO",   desc: "My own everyday-goods brand." },
              { to: "/projects",  label: "Ventures",    desc: "What I build on my own." },
              { to: "/writing",   label: "Writing",     desc: "Essays on building and marketing." },
              { to: "/clients",   label: "Clients",     desc: "Businesses I've grown with." },
            ].map(({ to, label, desc }) => (
              <Link key={to} to={to} data-testid={`about-related-${label.toLowerCase()}`}
                className="group border border-border bg-background p-5 hover-lift flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <span className="font-display text-2xl group-hover:text-primary transition-colors">{label}</span>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <p className="text-sm text-muted-foreground">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
